import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * DotField — the moving dot grid behind the sign-in brand panel.
 *
 * A grid of white dots that breathe slowly. A soft lens drifts across the grid,
 * lifting and tinting the dots under it; when the pointer is over the panel the
 * lens follows it, and drifts again when the pointer leaves. Purely decorative:
 * hidden from assistive tech and never takes pointer events itself.
 *
 * Respects prefers-reduced-motion by drawing one still frame.
 * Fills its positioned parent; put it first inside a `relative overflow-hidden` element.
 */
export interface DotFieldProps {
  /** Distance between dots, in CSS pixels. */
  spacing?: number
  /** Lens colour as an RGB triple. Defaults to the Good Outcomes light cyan. */
  lensColor?: [number, number, number]
  className?: string
}

const GO_CYAN: [number, number, number] = [127, 211, 251]

interface Dot {
  x: number
  y: number
  phase: number
  size: number
}

export function DotField({
  spacing = 30,
  lensColor = GO_CYAN,
  className,
}: DotFieldProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas?.parentElement
    const ctx = canvas?.getContext("2d")
    if (!canvas || !host || !ctx) return

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const lerp = (a: number, b: number, k: number) => a + (b - a) * k
    const lens = { x: 0.62, y: 0.42, tx: 0.62, ty: 0.42, k: 0, tk: 0, auto: true }
    let dots: Dot[] = []
    let width = 0
    let height = 0
    let t = 0
    let raf = 0

    const size = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = host.getBoundingClientRect()
      width = canvas.width = Math.round(rect.width * dpr)
      height = canvas.height = Math.round(rect.height * dpr)
      const sp = spacing * dpr
      const ox = (width % sp) / 2
      const oy = (height % sp) / 2
      dots = []
      for (let y = oy; y <= height; y += sp)
        for (let x = ox; x <= width; x += sp)
          dots.push({ x, y, phase: Math.random() * Math.PI * 2, size: 0.7 + Math.random() * 0.6 })
    }

    const draw = () => {
      const dpr = window.devicePixelRatio || 1
      t += 1 / 60
      if (lens.auto) {
        lens.tx = 0.56 + 0.18 * Math.sin(t * 0.21) * Math.cos(t * 0.07)
        lens.ty = 0.5 + 0.26 * Math.sin(t * 0.16 + 1.3)
        lens.tk = 0.3
      }
      lens.x = lerp(lens.x, lens.tx, lens.auto ? 0.02 : 0.06)
      lens.y = lerp(lens.y, lens.ty, lens.auto ? 0.02 : 0.06)
      lens.k = lerp(lens.k, lens.tk, 0.04)

      ctx.clearRect(0, 0, width, height)
      const lx = lens.x * width
      const ly = lens.y * height
      const radius = Math.min(width, height) * 0.42
      const [lr, lg, lb] = lensColor

      for (const d of dots) {
        const f = Math.max(0, 1 - Math.hypot(d.x - lx, d.y - ly) / radius)
        const g = f * f * f * lens.k
        const breathe = 0.5 + 0.5 * Math.sin(t * 0.9 + d.phase)
        const alpha = Math.min(1, 0.14 + 0.07 * breathe + g * 0.9)
        const r = (1.1 * d.size + breathe * 0.25 + g * 4.2) * dpr
        ctx.beginPath()
        ctx.arc(d.x, d.y, r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${Math.round(lerp(255, lr, g))},${Math.round(lerp(255, lg, g))},${Math.round(lerp(255, lb, g))},${alpha.toFixed(3)})`
        ctx.fill()
      }
      if (!reduce) raf = requestAnimationFrame(draw)
    }

    const onMove = (e: PointerEvent) => {
      const rect = host.getBoundingClientRect()
      lens.tx = (e.clientX - rect.left) / rect.width
      lens.ty = (e.clientY - rect.top) / rect.height
      lens.tk = 1
      lens.auto = false
    }
    const onLeave = () => {
      lens.auto = true
    }

    size()
    if (reduce) {
      t = 3
      lens.k = 0.3
      draw()
    } else {
      raf = requestAnimationFrame(draw)
    }

    // Resizing the canvas clears it, so the reduced-motion still frame is redrawn.
    const resize = new ResizeObserver(() => {
      size()
      if (reduce) draw()
    })
    resize.observe(host)
    host.addEventListener("pointermove", onMove)
    host.addEventListener("pointerleave", onLeave)
    return () => {
      cancelAnimationFrame(raf)
      resize.disconnect()
      host.removeEventListener("pointermove", onMove)
      host.removeEventListener("pointerleave", onLeave)
    }
  }, [spacing, lensColor[0], lensColor[1], lensColor[2]])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-slot="dot-field"
      className={cn("pointer-events-none absolute inset-0 h-full w-full", className)}
    />
  )
}
